import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Mic,
  MicOff,
  Video as VideoIcon,
  VideoOff,
  SkipForward,
  X,
  Flag,
  Maximize,
  Loader,
  AlertCircle,
  Home,
  Eye,
  EyeOff,
  Volume2,
  Sparkles,
  PhoneOff,
  Smile,
  MessageSquare,
} from 'lucide-react';
import { toast } from 'sonner';
import { useVideoChat } from '@/hooks/useVideoChat';
import SEO from '@/components/SEO';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const VideoChat = () => {
  const navigate = useNavigate();
  const [session, setSession] = useState(null);

  useEffect(() => {
    const sessionData = localStorage.getItem('toonah_session');
    if (!sessionData) {
      navigate('/');
      return;
    }
    setSession(JSON.parse(sessionData));
  }, [navigate]);

  if (!session) return null;
  return (
    <>
      <SEO
        title="Video Chat — Toonah"
        description="Live anonymous video chat room."
        path="/chat"
        robots="noindex,nofollow"
      />
      <ChatSession session={session} navigate={navigate} />
    </>
  );
};

const ChatSession = ({ session, navigate }) => {
  const {
    phase,
    incomingReactions,
    chatMessages,
    sendReaction,
    sendChatMessage,
    localRevealed,
    remoteRevealed,
    audioEnabled,
    videoEnabled,
    needsAudioUnlock,
    peerSessionId,
    localVideoRef,
    remoteVideoRef,
    revealSelf,
    hideSelf,
    toggleAudio,
    toggleVideo,
    next,
    unlockAudio,
  } = useVideoChat(session);

  const [connectionTime, setConnectionTime] = useState(0);

  useEffect(() => {
    if (phase !== 'connected') {
      setConnectionTime(0);
      return;
    }
    const interval = setInterval(() => setConnectionTime((p) => p + 1), 1000);
    return () => clearInterval(interval);
  }, [phase]);

  const handleNext = () => {
    toast.info('Finding someone new...');
    next();
  };

  const [showEndConfirm, setShowEndConfirm] = useState(false);
  const [showReactions, setShowReactions] = useState(false);
  const [showChat, setShowChat] = useState(false);
  const [chatDraft, setChatDraft] = useState('');
  const [localFloats, setLocalFloats] = useState([]);
  const endInFlightRef = useRef(false);

  const handleEndChat = () => navigate('/');

  const requestEndChat = () => {
    // In an active call, ask for confirmation. Otherwise (searching / errors /
    // no peer yet) just leave immediately — nothing to protect.
    if (inCall) {
      setShowEndConfirm(true);
    } else {
      handleEndChat();
    }
  };

  const confirmEndChat = () => {
    if (endInFlightRef.current) return;
    endInFlightRef.current = true;
    setShowEndConfirm(false);
    handleEndChat();
  };

  // Close confirm dialog on Escape (desktop keyboard accessibility)
  useEffect(() => {
    if (!showEndConfirm) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') setShowEndConfirm(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [showEndConfirm]);

  const REACTIONS = [
    { emoji: '❤️', label: 'Love it' },
    { emoji: '😂', label: 'Funny' },
    { emoji: '👍', label: 'Nice' },
    { emoji: '😎', label: 'Awesome' },
    { emoji: '🤨', label: 'SUS' },
    { emoji: '👀', label: 'Reveal?' },
  ];

  const spawnLocalFloat = (emoji) => {
    const id = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    setLocalFloats((cur) => [...cur, { id, emoji }]);
    setTimeout(() => setLocalFloats((cur) => cur.filter((r) => r.id !== id)), 4000);
  };

  const handleReaction = (emoji) => {
    // 👀 is only a request — never triggers reveal automatically.
    // Picker intentionally stays OPEN so users can rapidly tap the same
    // reaction (e.g. ❤️❤️❤️❤️❤️) or mix reactions. Each tap = independent event.
    spawnLocalFloat(emoji);
    sendReaction(emoji);
  };

  // Reactions picker: close on outside click or Escape (but NOT on selection)
  const reactionsWrapRef = useRef(null);
  useEffect(() => {
    if (!showReactions) return undefined;
    const onDocPointer = (e) => {
      if (reactionsWrapRef.current && !reactionsWrapRef.current.contains(e.target)) {
        setShowReactions(false);
      }
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setShowReactions(false);
    };
    document.addEventListener('mousedown', onDocPointer);
    document.addEventListener('touchstart', onDocPointer);
    window.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocPointer);
      document.removeEventListener('touchstart', onDocPointer);
      window.removeEventListener('keydown', onKey);
    };
  }, [showReactions]);

  const sendChat = (e) => {
    e?.preventDefault?.();
    const text = chatDraft.trim();
    if (!text) return;
    sendChatMessage(text);
    setChatDraft('');
  };

  const handleReport = async () => {
    if (!peerSessionId) return;
    const reason = prompt('Why are you reporting this user?\n\n1. Nudity\n2. Harassment\n3. Violence\n4. Other');
    if (!reason) return;
    try {
      await fetch(`${BACKEND_URL}/api/report`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          reported_by_session: session.session_id,
          reported_session: peerSessionId,
          reason,
          description: '',
        }),
      });
      toast.success('Report submitted. Thank you for keeping Toonah safe.');
      handleNext();
    } catch {
      toast.error('Failed to submit report');
    }
  };

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen();
    } else {
      document.exitFullscreen();
    }
  };

  const onRevealClick = () => {
    revealSelf();
    toast.success('You are now visible to your match');
  };

  const onHideClick = () => {
    hideSelf();
    toast.info('You are hidden again');
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const inCall = phase === 'connected';
  const remoteBlurred = inCall && !remoteRevealed;

  return (
    <div className="relative w-full h-screen bg-black overflow-hidden">
      {/* Remote Video — always streaming; blurred live until the peer reveals */}
      <div className="absolute inset-0">
        <video
          ref={remoteVideoRef}
          autoPlay
          playsInline
          data-testid="remote-video"
          className={`remote-video w-full h-full object-cover ${remoteBlurred ? 'privacy-blurred' : ''} ${inCall ? 'opacity-100' : 'opacity-0'}`}
        />

        {/* Premium privacy overlay */}
        <AnimatePresence>
          {remoteBlurred && (
            <motion.div
              key="privacy-overlay"
              data-testid="privacy-overlay"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0, transition: { duration: 0.7 } }}
              className="absolute inset-0 pointer-events-none"
            >
              <div className="absolute inset-0 privacy-veil" />
              <div className="absolute inset-0 privacy-breathe" />

              <div className="absolute inset-0 flex items-center justify-center pointer-events-auto">
                <motion.div
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.2 }}
                  className="privacy-card text-center px-8 py-8 max-w-md mx-4 rounded-3xl"
                >
                  <div className="mx-auto w-16 h-16 rounded-full bg-white/10 backdrop-blur-md border border-white/20 flex items-center justify-center mb-5">
                    <Sparkles className="w-7 h-7 text-blue-300" />
                  </div>

                  <h3 className="text-2xl font-bold text-white mb-2">
                    Someone is here
                  </h3>
                  <p className="text-slate-200/90 text-base mb-1">
                    A real person is behind the blur — you can talk right now.
                  </p>
                  <div className="flex items-center justify-center gap-2 text-emerald-300 text-sm mb-6">
                    <Volume2 className="w-4 h-4" />
                    <span className="audio-live-dot" />
                    <span>Live audio connected</span>
                  </div>

                  {!localRevealed ? (
                    <button
                      onClick={onRevealClick}
                      data-testid="reveal-me-button"
                      className="px-8 py-4 bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-700 hover:to-blue-600 text-white rounded-2xl font-bold text-lg shadow-lg hover:shadow-blue-500/30 transition-all transform hover:scale-105"
                    >
                      <div className="flex items-center gap-3">
                        <Eye className="w-6 h-6" />
                        <span>Reveal Me</span>
                      </div>
                    </button>
                  ) : (
                    <div className="space-y-3">
                      <div className="flex items-center justify-center gap-2 text-green-400 text-sm">
                        <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse" />
                        <span className="font-semibold">They can see you</span>
                      </div>
                      <button
                        onClick={onHideClick}
                        data-testid="hide-me-button"
                        className="px-6 py-3 bg-white/10 hover:bg-white/20 border border-white/20 backdrop-blur-md text-white rounded-2xl font-medium transition-all"
                      >
                        <div className="flex items-center gap-2">
                          <EyeOff className="w-5 h-5" />
                          <span>Hide Me Again</span>
                        </div>
                      </button>
                    </div>
                  )}

                  <div className="flex justify-center gap-8 text-sm mt-6">
                    <div className={`flex items-center gap-2 ${localRevealed ? 'text-green-400' : 'text-slate-400'}`}>
                      <div className={`w-2.5 h-2.5 rounded-full ${localRevealed ? 'bg-green-400' : 'bg-slate-500'}`} />
                      <span>You {localRevealed ? 'visible' : 'hidden'}</span>
                    </div>
                    <div className={`flex items-center gap-2 ${remoteRevealed ? 'text-green-400' : 'text-slate-400'}`}>
                      <div className={`w-2.5 h-2.5 rounded-full ${remoteRevealed ? 'bg-green-400' : 'bg-slate-500'}`} />
                      <span>Match {remoteRevealed ? 'visible' : 'hidden'}</span>
                    </div>
                  </div>
                </motion.div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Non-call screens */}
        {!inCall && (
          <div className="absolute inset-0 flex items-center justify-center bg-slate-900/95">
            <div className="text-center">
              {(phase === 'initializing' || phase === 'searching' || phase === 'connecting') && (
                <motion.div
                  data-testid="searching-screen"
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                >
                  <Loader className="w-16 h-16 text-blue-500 mx-auto mb-4 animate-spin" />
                  <h2 className="text-2xl font-bold text-white mb-2">
                    {phase === 'connecting' ? 'Match found! Connecting...' : 'Searching for someone...'}
                  </h2>
                  <p className="text-slate-400">This usually takes a few seconds</p>
                </motion.div>
              )}

              {phase === 'peer_left' && (
                <motion.div
                  data-testid="peer-left-screen"
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="space-y-4"
                >
                  <AlertCircle className="w-16 h-16 text-yellow-500 mx-auto" />
                  <h2 className="text-2xl font-bold text-white mb-2">The other person has left</h2>
                  <div className="flex gap-4 justify-center">
                    <button
                      onClick={handleNext}
                      data-testid="find-someone-else-button"
                      className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-full font-bold transition-all"
                    >
                      Find Someone Else
                    </button>
                    <button
                      onClick={handleEndChat}
                      data-testid="return-home-button"
                      className="px-6 py-3 bg-slate-700 hover:bg-slate-600 text-white rounded-full font-bold transition-all"
                    >
                      Return Home
                    </button>
                  </div>
                </motion.div>
              )}

              {phase === 'media_error' && (
                <motion.div
                  data-testid="media-error-screen"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="space-y-4"
                >
                  <AlertCircle className="w-16 h-16 text-red-500 mx-auto" />
                  <h2 className="text-2xl font-bold text-white">Camera & microphone needed</h2>
                  <p className="text-slate-400">Please allow access and reload the page.</p>
                </motion.div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Audio unlock (browser autoplay policy) */}
      {inCall && needsAudioUnlock && (
        <button
          onClick={unlockAudio}
          data-testid="audio-unlock-button"
          className="absolute top-24 left-1/2 -translate-x-1/2 z-50 px-6 py-3 bg-emerald-600 hover:bg-emerald-700 text-white rounded-full font-bold shadow-xl flex items-center gap-2 animate-pulse"
        >
          <Volume2 className="w-5 h-5" />
          Tap to enable sound
        </button>
      )}

      {/* Local Video PiP */}
      <LocalVideoPiP localVideoRef={localVideoRef} localRevealed={localRevealed} inCall={inCall} />

      {/* Top Info Bar */}
      <div className="absolute top-6 left-1/2 -translate-x-1/2 glass-card px-6 py-3 rounded-full flex items-center space-x-4">
        {inCall && (
          <>
            <div className="flex items-center space-x-2" data-testid="connected-status">
              <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
              <span className="text-sm font-bold text-white">Connected</span>
            </div>
            <div className="w-px h-4 bg-white/20" />
            <span className="text-sm font-mono text-white">{formatTime(connectionTime)}</span>
          </>
        )}
      </div>

      {/* Reveal / Hide — kept separate above the main control bar (unchanged logic) */}
      {inCall && (
        <div className="absolute bottom-24 sm:bottom-28 left-1/2 -translate-x-1/2 z-30">
          {!localRevealed ? (
            <button
              onClick={onRevealClick}
              data-testid="reveal-toggle-button"
              className="reveal-cta reveal-cta-pulse px-4 sm:px-5 py-2 sm:py-2.5 rounded-full flex items-center gap-2 font-semibold text-xs sm:text-sm"
            >
              <Eye className="w-4 h-4" />
              <span>Reveal Me</span>
            </button>
          ) : (
            <button
              onClick={onHideClick}
              data-testid="reveal-toggle-button"
              className="hide-cta px-4 sm:px-5 py-2 sm:py-2.5 rounded-full flex items-center gap-2 font-semibold text-xs sm:text-sm"
            >
              <EyeOff className="w-4 h-4" />
              <span>Hide Me</span>
            </button>
          )}
        </div>
      )}

      {/* Floating reactions layer */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden z-40">
        {[...localFloats, ...incomingReactions].map((r) => (
          <span
            key={r.id}
            className="reaction-float absolute left-1/2 bottom-24 sm:bottom-32 text-4xl sm:text-5xl"
            style={{ '--drift': `${(Math.random() * 120 - 60) | 0}px` }}
          >
            {r.emoji}
          </span>
        ))}
      </div>

      {/* Control Bar (v3) */}
      <div
        className="absolute bottom-6 sm:bottom-8 left-1/2 -translate-x-1/2 ctrl-bar-v3 px-2.5 sm:px-4 py-2 sm:py-2.5 rounded-full flex items-center gap-1.5 sm:gap-2 max-w-[calc(100vw-1rem)] z-30"
        data-testid="control-bar"
      >
        {/* 1. Audio */}
        <button
          onClick={toggleAudio}
          data-testid="mute-mic-button"
          aria-label={audioEnabled ? 'Mute microphone' : 'Unmute microphone'}
          className={`ctrl3d ${audioEnabled ? '' : 'off'} p-2.5 sm:p-3 rounded-full`}
        >
          {audioEnabled ? <Mic className="w-4 h-4 sm:w-5 sm:h-5" /> : <MicOff className="w-4 h-4 sm:w-5 sm:h-5" />}
        </button>

        {/* 2. Camera */}
        <button
          onClick={toggleVideo}
          data-testid="turn-camera-off-button"
          aria-label={videoEnabled ? 'Turn camera off' : 'Turn camera on'}
          className={`ctrl3d ${videoEnabled ? '' : 'off'} p-2.5 sm:p-3 rounded-full`}
        >
          {videoEnabled ? <VideoIcon className="w-4 h-4 sm:w-5 sm:h-5" /> : <VideoOff className="w-4 h-4 sm:w-5 sm:h-5" />}
        </button>

        {/* 3. Next (pill, primary) */}
        <button
          onClick={handleNext}
          data-testid="next-chat-button"
          aria-label="Next match"
          className="ctrl3d-primary px-4 sm:px-5 py-2.5 sm:py-3 rounded-full flex items-center gap-1.5 sm:gap-2 text-sm font-bold"
        >
          <SkipForward className="w-4 h-4 sm:w-5 sm:h-5" />
          <span className="hidden xs:inline sm:inline">Next</span>
        </button>

        <div className="w-px h-6 sm:h-7 bg-white/10 mx-0.5" />

        {/* 4. Emoji / Reactions */}
        <div className="relative" ref={reactionsWrapRef}>
          <button
            onClick={() => { setShowReactions((s) => !s); setShowChat(false); }}
            data-testid="reactions-button"
            aria-label="Reactions"
            className="ctrl3d p-2.5 sm:p-3 rounded-full"
          >
            <Smile className="w-4 h-4 sm:w-5 sm:h-5" />
          </button>
          {showReactions && (
            <div
              className="absolute bottom-full mb-3 left-1/2 -translate-x-1/2 ctrl-bar-v3 p-2 rounded-2xl flex gap-1 z-50"
              data-testid="reactions-picker"
            >
              {REACTIONS.map((r) => (
                <button
                  key={r.emoji}
                  onClick={() => handleReaction(r.emoji)}
                  title={r.label}
                  data-testid={`reaction-${r.emoji}`}
                  className="ctrl3d w-10 h-10 sm:w-11 sm:h-11 rounded-full text-xl sm:text-2xl flex items-center justify-center"
                >
                  {r.emoji}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* 5. Live Chat */}
        <button
          onClick={() => { setShowChat((s) => !s); setShowReactions(false); }}
          data-testid="chat-button"
          aria-label="Live chat"
          className="ctrl3d p-2.5 sm:p-3 rounded-full relative"
        >
          <MessageSquare className="w-4 h-4 sm:w-5 sm:h-5" />
          {chatMessages.length > 0 && !showChat && (
            <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-blue-500 text-[10px] font-bold text-white flex items-center justify-center">
              {Math.min(chatMessages.length, 9)}
            </span>
          )}
        </button>

        {/* 6. Fullscreen */}
        <button
          onClick={toggleFullscreen}
          data-testid="fullscreen-button"
          aria-label="Toggle fullscreen"
          className="ctrl3d p-2.5 sm:p-3 rounded-full hidden sm:inline-flex"
        >
          <Maximize className="w-4 h-4 sm:w-5 sm:h-5" />
        </button>

        {/* 7. Report */}
        <button
          onClick={handleReport}
          data-testid="report-user-button"
          aria-label="Report user"
          className="ctrl3d p-2.5 sm:p-3 rounded-full"
        >
          <Flag className="w-4 h-4 sm:w-5 sm:h-5" />
        </button>

        {/* 8. End Call */}
        <button
          onClick={requestEndChat}
          data-testid="end-chat-button"
          aria-label="End conversation"
          className="ctrl3d-danger p-2.5 sm:p-3 rounded-full"
        >
          <PhoneOff className="w-4 h-4 sm:w-5 sm:h-5" />
        </button>
      </div>

      {/* Chat Panel */}
      {showChat && (
        <div
          className="absolute bottom-24 sm:bottom-28 right-4 sm:right-8 w-[min(360px,calc(100vw-2rem))] max-h-[60vh] ctrl-bar-v3 rounded-2xl flex flex-col z-40 shadow-2xl"
          data-testid="chat-panel"
        >
          <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
            <div className="flex items-center gap-2 text-white">
              <MessageSquare className="w-4 h-4" />
              <span className="text-sm font-semibold">Live chat</span>
              <span className="text-[11px] text-white/50">disappears when the call ends</span>
            </div>
            <button
              onClick={() => setShowChat(false)}
              aria-label="Close chat"
              className="text-white/60 hover:text-white text-xl leading-none"
            >×</button>
          </div>
          <div className="flex-1 overflow-y-auto p-3 space-y-2" data-testid="chat-messages">
            {chatMessages.length === 0 ? (
              <p className="text-xs text-white/40 text-center py-6">Say hi 👋</p>
            ) : chatMessages.map((m) => (
              <div key={m.id} className={`flex ${m.from === 'me' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[80%] px-3 py-2 rounded-2xl text-sm break-words ${
                  m.from === 'me'
                    ? 'bg-blue-600 text-white rounded-br-sm'
                    : 'bg-white/10 text-white rounded-bl-sm border border-white/10'
                }`}>{m.text}</div>
              </div>
            ))}
          </div>
          <form onSubmit={sendChat} className="p-2 border-t border-white/10 flex gap-2">
            <input
              value={chatDraft}
              onChange={(e) => setChatDraft(e.target.value.slice(0, 300))}
              placeholder="Type a message…"
              maxLength={300}
              data-testid="chat-input"
              className="flex-1 bg-white/5 border border-white/10 rounded-full px-4 py-2 text-sm text-white placeholder-white/40 focus:outline-none focus:border-blue-500"
            />
            <button
              type="submit"
              disabled={!chatDraft.trim()}
              data-testid="chat-send"
              className="ctrl3d-primary px-4 py-2 rounded-full text-sm font-semibold disabled:opacity-40 disabled:cursor-not-allowed"
            >Send</button>
          </form>
        </div>
      )}

      {/* Control Bar (legacy — removed) */}
      <div style={{display:'none'}} data-testid="control-bar-legacy-removed">
        {/* Tertiary: Mic */}
        <button
          onClick={toggleAudio}
          data-testid="mute-mic-button"
          title={audioEnabled ? 'Mute microphone' : 'Unmute microphone'}
          className={`control-icon-btn p-2.5 sm:p-3 rounded-full ${audioEnabled ? '' : 'active-off'}`}
        >
          {audioEnabled ? <Mic className="w-4 h-4 sm:w-5 sm:h-5" /> : <MicOff className="w-4 h-4 sm:w-5 sm:h-5" />}
        </button>

        {/* Tertiary: Camera */}
        <button
          onClick={toggleVideo}
          data-testid="turn-camera-off-button"
          title={videoEnabled ? 'Turn camera off' : 'Turn camera on'}
          className={`control-icon-btn p-2.5 sm:p-3 rounded-full ${videoEnabled ? '' : 'active-off'}`}
        >
          {videoEnabled ? <VideoIcon className="w-4 h-4 sm:w-5 sm:h-5" /> : <VideoOff className="w-4 h-4 sm:w-5 sm:h-5" />}
        </button>

        {inCall && (
          <>
            <div className="control-divider mx-0.5 sm:mx-1" />

            {/* PRIMARY CTA: Reveal / Hide */}
            {!localRevealed ? (
              <button
                onClick={onRevealClick}
                data-testid="reveal-toggle-button"
                title="Reveal me"
                className="reveal-cta reveal-cta-pulse px-4 sm:px-6 py-2.5 sm:py-3 rounded-full flex items-center gap-2 font-semibold text-sm sm:text-base"
              >
                <Eye className="w-4 h-4 sm:w-5 sm:h-5" />
                <span>Reveal Me</span>
              </button>
            ) : (
              <button
                onClick={onHideClick}
                data-testid="reveal-toggle-button"
                title="Hide me"
                className="hide-cta px-4 sm:px-5 py-2.5 sm:py-3 rounded-full flex items-center gap-2 font-semibold text-sm sm:text-base"
              >
                <EyeOff className="w-4 h-4 sm:w-5 sm:h-5" />
                <span>Hide Me</span>
              </button>
            )}
          </>
        )}

        {/* Secondary: Next */}
        <button
          onClick={handleNext}
          data-testid="next-chat-button"
          title="Next match"
          className="control-secondary-btn px-3 sm:px-4 py-2.5 sm:py-3 rounded-full flex items-center gap-1.5 sm:gap-2 text-sm font-semibold"
        >
          <SkipForward className="w-4 h-4 sm:w-5 sm:h-5" />
          <span className="hidden sm:inline">Next</span>
        </button>

        <div className="control-divider mx-0.5 sm:mx-1" />

        {/* Tertiary: Report */}
        <button
          onClick={handleReport}
          data-testid="report-user-button"
          title="Report user"
          className="control-icon-btn p-2.5 sm:p-3 rounded-full"
        >
          <Flag className="w-4 h-4 sm:w-5 sm:h-5" />
        </button>

        {/* Tertiary: Fullscreen (hidden on very small screens to save space) */}
        <button
          onClick={toggleFullscreen}
          data-testid="fullscreen-button"
          title="Toggle fullscreen"
          className="control-icon-btn p-2.5 sm:p-3 rounded-full hidden sm:inline-flex"
        >
          <Maximize className="w-4 h-4 sm:w-5 sm:h-5" />
        </button>

        {/* Tertiary danger: End */}
        <button
          onClick={requestEndChat}
          data-testid="end-chat-button"
          aria-label="End conversation"
          title="End conversation"
          className="control-icon-btn danger p-2.5 sm:p-3 rounded-full"
        >
          <PhoneOff className="w-4 h-4 sm:w-5 sm:h-5" />
        </button>
      </div>

      {/* Home Button */}
      <button
        onClick={requestEndChat}
        data-testid="home-button"
        aria-label="Leave"
        className="absolute top-6 right-6 p-3 control-bar-glass rounded-full hover:brightness-125 transition-all text-white"
      >
        <Home className="w-5 h-5 text-white" />
      </button>

      {/* End Conversation confirmation */}
      <AnimatePresence>
        {showEndConfirm && (
          <motion.div
            key="end-confirm-backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
            className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 backdrop-blur-sm px-4"
            data-testid="end-confirm-backdrop"
            role="presentation"
          >
            <motion.div
              key="end-confirm-dialog"
              initial={{ opacity: 0, scale: 0.94, y: 12 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.96, y: 8 }}
              transition={{ duration: 0.18, ease: 'easeOut' }}
              className="control-bar-glass rounded-2xl p-6 sm:p-7 max-w-sm w-full text-white shadow-2xl"
              role="dialog"
              aria-modal="true"
              aria-labelledby="end-confirm-title"
              aria-describedby="end-confirm-desc"
              data-testid="end-confirm-dialog"
              onClick={(e) => e.stopPropagation()}
            >
              <h2 id="end-confirm-title" className="text-xl sm:text-2xl font-bold mb-2">
                End this conversation?
              </h2>
              <p id="end-confirm-desc" className="text-sm text-white/70 mb-6">
                This will end the conversation for both of you.
              </p>
              <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-3">
                <button
                  onClick={() => setShowEndConfirm(false)}
                  data-testid="end-confirm-cancel"
                  className="control-secondary-btn px-5 py-2.5 rounded-full text-sm font-semibold"
                  autoFocus
                >
                  Keep Talking
                </button>
                <button
                  onClick={confirmEndChat}
                  data-testid="end-confirm-accept"
                  disabled={endInFlightRef.current}
                  className="px-5 py-2.5 rounded-full text-sm font-semibold bg-red-500 hover:bg-red-600 disabled:opacity-60 disabled:cursor-not-allowed text-white shadow-[0_8px_24px_-8px_rgba(239,68,68,0.55)] transition"
                >
                  End Conversation
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const LocalVideoPiP = ({ localVideoRef, localRevealed, inCall }) => {
  const [pos, setPos] = useState({ x: window.innerWidth - 270, y: window.innerHeight - 210 });
  const [size, setSize] = useState(() => {
    const saved = localStorage.getItem('toonah_local_video_size');
    return saved ? JSON.parse(saved) : { width: 240, height: 180 };
  });
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [isResizing, setIsResizing] = useState(false);
  const [resizeStart, setResizeStart] = useState({ x: 0, y: 0, width: 0, height: 0 });

  const handleDragStart = (e) => {
    setIsDragging(true);
    const clientX = e.type === 'touchstart' ? e.touches[0].clientX : e.clientX;
    const clientY = e.type === 'touchstart' ? e.touches[0].clientY : e.clientY;
    setDragOffset({ x: clientX - pos.x, y: clientY - pos.y });
  };

  const handleResizeStart = (e) => {
    e.stopPropagation();
    setIsResizing(true);
    const clientX = e.type === 'touchstart' ? e.touches[0].clientX : e.clientX;
    const clientY = e.type === 'touchstart' ? e.touches[0].clientY : e.clientY;
    setResizeStart({ x: clientX, y: clientY, width: size.width, height: size.height });
  };

  useEffect(() => {
    if (!isDragging) return;
    const move = (e) => {
      e.preventDefault();
      const clientX = e.type === 'touchmove' ? e.touches[0].clientX : e.clientX;
      const clientY = e.type === 'touchmove' ? e.touches[0].clientY : e.clientY;
      setPos({ x: clientX - dragOffset.x, y: clientY - dragOffset.y });
    };
    const end = () => setIsDragging(false);
    window.addEventListener('mousemove', move);
    window.addEventListener('mouseup', end);
    window.addEventListener('touchmove', move, { passive: false });
    window.addEventListener('touchend', end);
    return () => {
      window.removeEventListener('mousemove', move);
      window.removeEventListener('mouseup', end);
      window.removeEventListener('touchmove', move);
      window.removeEventListener('touchend', end);
    };
  }, [isDragging, dragOffset]);

  useEffect(() => {
    if (!isResizing) return;
    const move = (e) => {
      e.preventDefault();
      const clientX = e.type === 'touchmove' ? e.touches[0].clientX : e.clientX;
      const clientY = e.type === 'touchmove' ? e.touches[0].clientY : e.clientY;
      const newWidth = Math.max(150, Math.min(window.innerWidth * 0.4, resizeStart.width + (clientX - resizeStart.x)));
      const newHeight = Math.max(100, Math.min(window.innerHeight * 0.4, resizeStart.height + (clientY - resizeStart.y)));
      setSize({ width: newWidth, height: newHeight });
    };
    const end = () => {
      setIsResizing(false);
      setSize((s) => {
        localStorage.setItem('toonah_local_video_size', JSON.stringify(s));
        return s;
      });
    };
    window.addEventListener('mousemove', move);
    window.addEventListener('mouseup', end);
    window.addEventListener('touchmove', move, { passive: false });
    window.addEventListener('touchend', end);
    return () => {
      window.removeEventListener('mousemove', move);
      window.removeEventListener('mouseup', end);
      window.removeEventListener('touchmove', move);
      window.removeEventListener('touchend', end);
    };
  }, [isResizing, resizeStart]);

  return (
    <div
      className="fixed rounded-xl overflow-hidden border-3 border-white/30 shadow-2xl cursor-move z-50 bg-black"
      data-testid="local-video-pip"
      style={{
        left: `${pos.x}px`,
        top: `${pos.y}px`,
        width: `${size.width}px`,
        height: `${size.height}px`,
        transition: isDragging || isResizing ? 'none' : 'all 0.2s ease',
      }}
      onMouseDown={handleDragStart}
      onTouchStart={handleDragStart}
    >
      <video
        ref={localVideoRef}
        autoPlay
        playsInline
        muted
        data-testid="local-video"
        className="w-full h-full object-cover"
      />
      {inCall && (
        <div
          data-testid="local-reveal-badge"
          className={`absolute top-2 left-2 px-2 py-1 rounded-full text-[11px] font-semibold backdrop-blur-md flex items-center gap-1 ${
            localRevealed ? 'bg-green-500/70 text-white' : 'bg-black/60 text-slate-200'
          }`}
        >
          {localRevealed ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
          {localRevealed ? 'Visible to match' : 'Hidden from match'}
        </div>
      )}
      <div
        className="absolute bottom-0 right-0 w-8 h-8 cursor-se-resize bg-white/20 hover:bg-white/40 transition-colors"
        style={{ clipPath: 'polygon(100% 0, 100% 100%, 0 100%)' }}
        onMouseDown={handleResizeStart}
        onTouchStart={handleResizeStart}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="absolute bottom-1 right-1 w-1 h-1 bg-white rounded-full" />
        <div className="absolute bottom-1 right-2.5 w-1 h-1 bg-white rounded-full" />
        <div className="absolute bottom-2.5 right-1 w-1 h-1 bg-white rounded-full" />
      </div>
    </div>
  );
};

export default VideoChat;
